import * as apiWarehouse from 'api/apiWarehouses';
import type {
    DemandProduct,
    ProductPreviewRow,
    WarehouseSourceKey,
    WarehouseSourceOption,
} from './types';

const isCustomSourceKey = (key: WarehouseSourceKey) =>
    key === 'custom' || String(key).startsWith('custom_');

export const fetchWarehouseStocksMap = async (
    warehouseId: number,
): Promise<Map<number, number>> => {
    const map = new Map<number, number>();
    if (!(warehouseId > 0)) return map;
    const res = await apiWarehouse.getStocks(warehouseId, {
        page: 1,
        onPage: 999999,
        showAll: false,
    });
    const stocks = res?.stocks || [];
    for (const stock of stocks) {
        const productId = Number(stock.productId || 0);
        const stockCount = Number(stock.stockCount || 0);
        if (!productId || !(stockCount > 0)) continue;
        map.set(productId, (map.get(productId) || 0) + stockCount);
    }
    return map;
};

export interface AllocateFromWarehousesParams {
    demand: DemandProduct[];
    enabledSources: WarehouseSourceOption[];
    objectWarehouseId: number;
    skipExisting: boolean;
    existingLabel: string;
}

/**
 * Алокація залишків: резерв → бригада → користувацькі → основний.
 * При skipExisting спочатку враховує наявність на складі об'єкта.
 */
export const allocateFromWarehouses = async ({
    demand,
    enabledSources,
    objectWarehouseId,
    skipExisting,
    existingLabel,
}: AllocateFromWarehousesParams): Promise<ProductPreviewRow[]> => {
    const pick = (key: WarehouseSourceKey) =>
        enabledSources.find((s) => s.key === key && s.available && s.warehouseId > 0);

    const customSources = enabledSources.filter(
        (s) => isCustomSourceKey(s.key) && s.available && s.warehouseId > 0,
    );

    const ordered = [
        pick('reserve'),
        pick('team'),
        ...customSources,
        pick('main'),
    ].filter(Boolean) as WarehouseSourceOption[];

    const stockByWarehouse = new Map<number, Map<number, number>>();
    for (const source of ordered) {
        const stocks = await fetchWarehouseStocksMap(source.warehouseId);
        stockByWarehouse.set(source.warehouseId, stocks);
    }

    const objectStocks =
        skipExisting && objectWarehouseId > 0
            ? await fetchWarehouseStocksMap(objectWarehouseId)
            : new Map<number, number>();

    const rows: ProductPreviewRow[] = [];

    for (const item of demand) {
        let remain = item.need;
        let allocatedAny = false;

        if (skipExisting) {
            const already = Number(objectStocks.get(item.productId) || 0);
            const covered = Math.min(remain, already);
            if (covered > 0) {
                remain -= covered;
                allocatedAny = true;
                rows.push({
                    key: `${item.productId}-existing`,
                    productId: item.productId,
                    prodName: item.prodName,
                    prodArticle: item.prodArticle,
                    vendorName: item.vendorName,
                    need: item.need,
                    availability: already,
                    transferCount: 0,
                    existingCount: covered,
                    alreadyOnObject: true,
                    warehouseId: objectWarehouseId,
                    sourceKey: null,
                    warehouseLabel: existingLabel,
                    priceSale: item.priceSale,
                    selected: false,
                });
            }
        }

        for (const source of ordered) {
            const stockMap = stockByWarehouse.get(source.warehouseId);
            const available = Number(stockMap?.get(item.productId) || 0);
            if (remain <= 0 || available <= 0) continue;

            const take = Math.min(remain, available);
            remain -= take;
            allocatedAny = true;

            rows.push({
                key: `${item.productId}-${source.warehouseId}`,
                productId: item.productId,
                prodName: item.prodName,
                prodArticle: item.prodArticle,
                vendorName: item.vendorName,
                need: item.need,
                availability: available,
                transferCount: take,
                warehouseId: source.warehouseId,
                sourceKey: source.key,
                warehouseLabel: source.label,
                priceSale: item.priceSale,
                selected: take > 0,
            });
        }

        if (!allocatedAny) {
            rows.push({
                key: `${item.productId}-none`,
                productId: item.productId,
                prodName: item.prodName,
                prodArticle: item.prodArticle,
                vendorName: item.vendorName,
                need: item.need,
                availability: 0,
                transferCount: 0,
                warehouseId: 0,
                sourceKey: null,
                warehouseLabel: '—',
                priceSale: item.priceSale,
                selected: false,
            });
        }
    }

    return rows;
};
